import React from 'react';
import { roleLabel, personaLabel } from '../../utils/permissions';

/**
 * RoleStackBadge — compact visual of a user's full role identity (DOS-213).
 *
 * Shows platform role + optional persona chip. When per-location overrides exist
 * (passed via `overrides`), they render as small "@ Location" chips.
 *
 * Sizing matches existing role badges on AdminUsersPage — small, muted palette.
 */
export default function RoleStackBadge({
  role,
  persona,
  overrides = [],
  compact = false,
}) {
  if (!role && !persona && overrides.length === 0) return null;

  const gap = compact ? 'gap-1' : 'gap-1.5';
  const textSize = compact ? 'text-[10px]' : 'text-xs';
  const px = compact ? 'px-1.5' : 'px-2';
  const py = compact ? 'py-0.5' : 'py-0.5';

  return (
    <div className={`inline-flex flex-wrap items-center ${gap}`}>
      {role && (
        <span
          className={`inline-flex items-center ${px} ${py} ${textSize} font-medium rounded-full
            bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300`}
          title={`Platform role: ${role}`}
        >
          {roleLabel(role)}
        </span>
      )}
      {persona && (
        <span
          className={`inline-flex items-center ${px} ${py} ${textSize} font-medium rounded-full
            bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300`}
          title={`Default persona: ${persona}`}
        >
          {personaLabel(persona)}
        </span>
      )}
      {overrides.map(o => (
        <span
          key={o.location_id}
          className={`inline-flex items-center ${px} ${py} ${textSize} font-medium rounded-full
            bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300`}
          title={`Persona override at ${o.location_name}: ${o.persona}`}
        >
          {personaLabel(o.persona)} <span className="mx-1 text-purple-500">@</span> {o.location_name}
        </span>
      ))}
    </div>
  );
}
